/**
 * Shared autofill utilities for VVMC/Veeva doc-metadata driven form population.
 * Used by both the create (StepMetadataRedesigned) and clone (StepMetadataCloneRedesigned) flows.
 */

/**
 * Typed shape for the VVMC document metadata payload returned from /api/v2/veeva/doc-metadata/{veapId}.
 * Fields are optional because the backend may omit absent values.
 */
export type VeevaDocMetadata = {
  disease?: string;
  therapeuticArea?: string;
  modelType?: string;
  classification?: string;
  country?: string;
  owner?: string;
  owners?: string[];
  version?: string;
};

/**
 * Checks if a form value is considered empty.
 * Handles empty string, empty array, undefined, and null.
 */
export const isEmptyValue = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
};

/**
 * Resolves the country string and global flag from VVMC doc metadata country.
 * - null / blank / 'Global' (case-insensitive) → country = '', isGlobal = true
 * - any other non-blank value → country = rawValue, isGlobal = false
 */
export const resolveCountryGlobal = (
  docMetadataCountry: string | null | undefined,
): {country: string; isGlobal: boolean} => {
  const raw = typeof docMetadataCountry === 'string' ? docMetadataCountry.trim() : '';
  const isGlobal = !raw || raw.toLowerCase() === 'global';
  // For global models the country metadata key must be empty string — "Global" is not a valid
  // country option in the backend validator. The isGlobal flag drives the global toggle separately.
  return {country: isGlobal ? '' : raw, isGlobal};
};
